<template>
  <v-card>
    <v-card-title class="pb-0 pt-2"><!-- if icons on right, then pt-1 -->
      <h2>{{ $t('Dangerous goods') }}</h2>
    </v-card-title>
    <v-card-text>
      <v-alert :value="!allowAdr" type="warning">{{ $t('ADR.palletRequired') }}</v-alert>

      <v-autocomplete
        :search-input.sync="search"
        :items="items"
        v-model="item"
        item-text="name"
        item-value="name"
        :label="$t('Base')"
        :loading="loading"
        :disabled="!allowAdr"
        prepend-inner-icon="playlist_add"
        return-object
        cache-items
        hide-no-data
        clearable
        ref="input"
        @input="selectItem"
        @dblclick="search = '?'"
        item-disabled="forbidden"
      >
        <template #item="{ item }">
          <span>{{ item.name }}</span>
          <span v-if="item.forbidden" class="text-right ml-3 red--text"><v-icon color="error">no_transfer</v-icon> {{ $t('forbidden for transportation') }}</span>
        </template>
      </v-autocomplete>


      <v-data-table
        :headers="headers"
        :items="model"
        item-key="key"
        hide-actions
        :no-data-text="$t('No data')"
        class="elevation-2 dangr-goods-table"
        ref="dangrGoodsTable"
      >
        <template slot="items" slot-scope="props">
          <tr @click="props.expanded = !props.expanded" :id="props.item.key" :class="props.expanded ? 'expanded' : 'collapsed'">
            <td class="text-xs-left font-weight-bold">
              {{ props.item.number_undg }}
            </td>
            <td class="text-xs-left text-sm-center">
              {{ props.item.weight }} {{ $t('kg') }}
            </td>
            <td class="font-weight-bold">
              {{ props.item.technical_name }}
            </td>
            <td>
              {{ props.item.code_classification }}
            </td>
            <td class="text-xs-right text-sm-center">
              {{ props.item.code_hazard }}
            </td>
            <td class="text-xs-center pr-3">
              <v-tooltip bottom>
                <v-icon slot="activator" color="error" @click.stop="removeRow(props.item)">remove_circle</v-icon><!-- delete_outline -->
                {{ $t('Remove from list') }}
              </v-tooltip>
            </td>
          </tr>
        </template>
        <template slot="expand" slot-scope="{ item }">
          <v-form v-model="valid" :ref="item.key">

          <v-container fluid grid-list-lg class="DISBLexpanded-card">
            <v-layout row wrap class="align-center">
              <v-flex xs8 sm6 md4 class="font-weight-bold">
                <v-text-field :value="getName(item)" :label="$t('Base')"
                  readonly
                ></v-text-field>
              </v-flex>
              <v-flex xs4 sm3 md2>
                <v-text-field v-model="item.number_undg" :label="$t('UNDG number') + ' *'"
                  required :rules="[rule.required]"
                  readonly
                ></v-text-field>
              </v-flex>
              <v-flex xs4 sm3 md2 class="text-xs-left text-sm-center">
                <v-text-field v-model="item.weight" :label="$t('Weight') + ' *'"
                  :suffix="$t('kg')"
                  type="number" step="0.1"
                  required :rules="[rule.required]"
                  autofocus
                ></v-text-field>
              </v-flex>
              <v-flex xs4 sm3 md2 class="text-xs-right text-sm-center">
                <v-text-field v-model="item.code_hazard" :label="$t('Hazard code')" readonly></v-text-field>
              </v-flex>
              <v-flex xs4 sm3 md2>
                <v-text-field v-model="item.code_classification" :label="$t('Classification')" readonly></v-text-field>
              </v-flex>
              <v-flex xs8 sm6 md6>
                <v-text-field v-model="item.technical_name" :label="$t('Technical name')" readonly></v-text-field>
              </v-flex>
              <v-flex xs4 sm4 md2>
                <v-text-field v-model="item.property" :label="$t('Property')"></v-text-field>
              </v-flex>
              <v-flex xs6 sm4 md4>
                <v-text-field v-model="item.main_substance" :label="$t('Main substance')"></v-text-field>
              </v-flex>

              <v-flex xs6 sm4 md3>
                <v-select v-model="item.packing_group" :label="$t('Packing group (I, II, III)')"
                  :items="PACKING_GROUP"
                ></v-select>
              </v-flex>
              <v-flex xs6 sm5 md3>
                <packaging-dgs v-model="item.packaging" :label="$t('Packaging')" />
              </v-flex>
              <v-flex xs4 sm2 md2>
                <v-text-field v-model="item.packages_number" :label="$t('Packages')"
                  type="number"
                ></v-text-field>
              </v-flex>
              <v-flex xs8 sm5 md4>
                <!-- FIXME: for test purposes, use "quantity" instead "type" -->
                <v-select v-model="item.type" :label="$t('Quantity (EQ or LQ)')"
                  :items="QUANTITY"
                ></v-select>
              </v-flex>

              <v-subheader>{{ $t('Labels required') }}</v-subheader>
              <v-flex xs12>
                <v-layout row wrap align-end>
                  <v-flex xs4 v-if="item.label_1" class="text-xs-center">
                    <img :src="getImage(item.label_1)" style="max-width: 75%; max-height: 75%;" class="mb-2" />
                    <v-text-field v-model="item.label_1" :label="$t('Hazard label 1')" readonly append-icon="download" @click:append="downloadImage(item.label_1)"></v-text-field>
                  </v-flex>
                  <v-flex xs4 v-if="item.label_2" class="text-xs-center">
                    <img :src="getImage(item.label_2)" style="max-width: 75%; max-height: 75%;" class="mb-2" />
                    <v-text-field v-model="item.label_2" :label="$t('Hazard label 2')" readonly append-icon="download" @click:append="downloadImage(item.label_2)"></v-text-field>
                  </v-flex>
                  <v-flex xs4 v-if="item.label_3" class="text-xs-center">
                    <img :src="getImage(item.label_3)" style="max-width: 75%; max-height: 75%;" class="mb-2" />
                    <v-text-field v-model="item.label_3" :label="$t('Hazard label 3')" readonly append-icon="download" @click:append="downloadImage(item.label_3)"></v-text-field>
                  </v-flex>
                </v-layout>
              </v-flex>
            </v-layout>
          </v-container>

          </v-form>
        </template>
      </v-data-table>


    </v-card-text>
    <v-card-actions>
      <v-btn flat color="grey" @click="cancel"><v-icon>cancel</v-icon>&nbsp;{{ $t('Cancel') }}</v-btn>
      <v-spacer></v-spacer>
      <v-btn flat color="blue darken-1" @click="done" :disabled="model.length > 0 && !valid"><v-icon>done</v-icon>&nbsp;{{ $t('OK') }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import Vue from 'vue';
import { debounce } from 'lodash';
import { suggest, resources, dangerousGoodsImagePath }  from '@/resources';
import PackagingDgs from '@/components/SuggestPackagingDgs.vue';
import { downloadFile, extractBodyErrorMessages } from "@/components/util.js";

export default {
  props: {
    allowAdr: {
      type: Boolean,
      default: true
    },
    'value': {
      type: Array,
      default: () => []
    },
    'shown': {
      type: Boolean
    }
  },
  components: {
    PackagingDgs
  },
  data() {
    return {
      loading: false,
      headers: [
        { value: 'number_undg',         text: this.$t('UNDG number'),     class: 'font-weight-light' },
        { value: 'weight',              text: this.$t('Weight'),         class: 'font-weight-light' },
        { value: 'technical_name',      text: this.$t('Technical name'), class: 'font-weight-light' },
        { value: 'code_classification', text: this.$t('Classification'), class: 'font-weight-light' },
        { value: 'code_hazard',         text: this.$t('Hazard code'),     class: 'font-weight-light' },
      ],
      items: [],
      item: {},
      model: [],
      search: '',
      valid: null,
      rule: {
        required: val => !!val || this.$t('This field is required!')
      },
      PACKING_GROUP: [
        { value: 'GREAT_DANGER',  text: this.$t('Great danger')  },
        { value: 'MEDIUM_DANGER', text: this.$t('Medium danger') },
        { value: 'MINOR_DANGER',  text: this.$t('Minor danger')  },
      ],
      QUANTITY: [
        { value: 'EQ', text: this.$t('Exception (EQ)') },
        { value: 'LQ', text: this.$t('Limited (LQ)') },
      ]
    };
  },
  computed: {
    validateAll() {
      let areValid = 0;
      let formrefs = Object.keys( this.$refs ).filter(elem => elem.startsWith('key'));
      console.log(formrefs);
      formrefs.forEach(elem => {
        areValid += (this.$refs[elem] && this.$refs[elem].validate() ? 1 : 0);
      });
      return areValid;
    },
  },
  methods: {
    suggestImpl(search) {
      search && search.trim() !== '' && search !== this.item.name && this.querySelections(search);
    },
    init() {
      this.model = JSON.parse(JSON.stringify(this.value || []));
      this.model.forEach((elem, index) => {
        elem.key = this.getNextKey(index);
      });
    },
    querySelections(filter) {
      if (this.loading)
        return;
      this.loading = true;

      suggest.dangerousGoods({
        filter
      }).then(res => {
        this.items = res.body.suggestions;
      }).catch(err => {
        console.log('Cannot load dangerous goods bases (templates)', err);
        this.items = [];
      }).finally(() => {
        this.loading = false;
      });
    },
    selectItem() {
      if (!this.allowAdr) {
        this.$emit('blocked');
        return;
      }
      if (!this.item || !Object.keys(this.item).length)
        return;
      let item = JSON.parse(JSON.stringify(this.item));
      item.key = this.getNextKey(this.model.length);
      if (item.id)
        delete item.id;
      this.model.push(item);

      this.item = {};
      this.search = '';

      this.collapseAllRows();
      this.$set(this.$refs.dangrGoodsTable.expanded, item.key, true);
      setTimeout(() => {
        this.$vuetify.goTo('#' + item.key);
      }, 500);
    },
    getImage(name) {
      return Vue.http.options.root + '/' + dangerousGoodsImagePath + '?name=' + name;
    },
    downloadImage(name) {
      if (this.loading) {
        return;
      }
      this.loading = true;
      resources.dangerousGoodsImage({ name }).then((response) => {
        downloadFile(response);
      }).catch((err) => {
        console.log('Error downloading file(s)', err);
      }).finally(() => {
        this.loading = false;
      });
    },
    collapseAllRows() {
      this.model.forEach(elem => {
        this.$set(this.$refs.dangrGoodsTable.expanded, elem.key, false);
      });
    },
    removeRow(item) {
      const index = this.model.indexOf(item);
      this.model.splice(index, 1);
    },
    done() {
      if (!this.allowAdr && this.model.length) {
        this.$emit('blocked');
        return;
      }
      let model = JSON.parse(JSON.stringify(this.model));
      model.forEach(elem => {
        delete elem.key;
      });
      this.$emit('input', model);
      this.$emit('done');
    },
    cancel() {
      this.$emit('done');
    },
    getNextKey(index) {
      return 'key-' + (index + 1);
    },
    getName(item) {
      return item.name || item.base && item.base.name || '';
    }
  },
  created() {
    this.suggest = debounce(this.suggestImpl, 500);
  },
  mounted() {
    (!this.model || this.model && !this.model.length) && this.value && this.init();
  },
  watch: {
    search(val) {
      if (!val || !val.trim())
        return;
      this.suggest(val);
      if (val === '?') {
        this.search = '';
      }
    },
    value(val) {
      (!this.model || this.model && !this.model.length) && this.init();
    },
    shown(val) {
      if (!!val) {
        this.init();
      }
    }
  },
}
</script>

<style scoped>
.dangr-goods-table td {
  line-height: 1.1rem;
}
.dangr-goods-table tr.collapsed:hover > td {
  cursor: zoom-in;
}
.dangr-goods-table tr.expanded:hover > td {
  cursor: zoom-out;
}
.expanded-card {
  border-bottom: 1px dotted rgba(0,0,0,0.12);
}
.expanded-card:last-child {
  border-bottom-style: solid;
}
</style>
